import { ApiService } from '../../api/api-service';
import { DealerLocation } from '../../types';

const SERVICE_ROOT = '/location';

/**
 * Service for location-related API operations
 */
export class LocationService extends ApiService {
  /**
   * Get all locations for a dealer
   * @param dealerId Dealer ID
   * @returns Promise resolving to the locations array
   */
  async getLocations(dealerId: number): Promise<DealerLocation[]> {
    return this.handleApiCall(async () => {
      return this.client.get(`${SERVICE_ROOT}/dealer/${dealerId}`);
    }, 'Error fetching locations');
  }

  /**
   * Get a location by ID
   * @param id Location ID
   * @returns Promise resolving to a location
   */
  async getLocationById(id: string): Promise<DealerLocation> {
    return this.handleApiCall(async () => {
      return this.client.get(`${SERVICE_ROOT}/${id}`);
    }, `Error fetching location with ID ${id}`);
  }

  /**
   * Create a new location
   * @param location Location data
   * @returns Promise resolving to the created location
   */
  async createLocation(location: Omit<DealerLocation, 'id'>): Promise<DealerLocation> {
    return this.handleApiCall(
      () => this.client.post(`${SERVICE_ROOT}/dealer/${location.dealerId}`, location),
      'Error creating location'
    );
  }

  /**
   * Update a location
   * @param dealerId Dealer ID
   * @param locationId Location ID
   * @param location Partial location data to update
   * @returns Promise resolving when the location is updated
   */
  async updateLocation(
    dealerId: number,
    locationId: number,
    location: Partial<DealerLocation>
  ): Promise<void> {
    return this.handleApiCall(
      () => this.client.put(`${SERVICE_ROOT}/dealer/${dealerId}/${locationId}`, location),
      `Error updating location with ID ${locationId}`
    );
  }

  /**
   * Delete a location
   * @param id Location ID
   * @returns Promise resolving when the location is deleted
   */
  async deleteLocation(id: string): Promise<void> {
    return this.handleApiCall(async () => {
      return this.client.delete(`${SERVICE_ROOT}/${id}`);
    }, `Error deleting location with ID ${id}`);
  }
}
